import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";

import { useToast } from "@/components/ui/use-toast";
import { useConfirmDialog, useI18n } from "@/context";

import * as clientsApi from "./clients.api";
import { IUseClients } from "./clients.hooks.interface";
import {
  clientPartnerPreRegisterEditSchema,
  clientPreRegisterFormSchema,
  rejectionReasonSchema,
  TClientPreRegisterFormData,
  TRejectionReason,
} from "./clients.schema";
import {
  ETypeOfPartner,
  TClient,
  TClientExtended,
  TClientExtendedResponse,
  TRejectionData,
  TUpdateClientInput,
} from "./clients.types";
import * as plansApi from "./plans.api";
import { usePlans } from "./plans.hooks";

function businessClassificationToFormValue(bc: string | undefined): string {
  if (!bc) return "servicos";
  if (bc === "comercial") return "comercio";
  if (bc === "comercio" || bc === "industria" || bc === "servicos" || bc === "rural") {
    return bc;
  }
  return "servicos";
}

function emptyPartnerFormValues(): TClientPreRegisterFormData {
  return {
    name: "",
    cnpj: "",
    corporate_name: "",
    email: "",
    type_partner: undefined,
    business_classification: "",
    address: {
      postal_code: "",
      street: "",
      number: "",
      complement: "",
      neighborhood: "",
      city: "",
      state: "",
    },
    company_representative: {
      first_name: "",
      last_name: "",
      personal_email: "",
      cpf: "",
      mobile_phone: "",
    },
    bank_account: {
      bank_name: "",
      bank_branch: "",
      checking_account: "",
      checking_account_digit: "",
      pix_key: "",
    },
    document: {
      company_logo: undefined,
      cnpj_card: undefined,
      signed_contract: [],
    },
  } as TClientPreRegisterFormData;
}

function normalizeSignedContractUrls(
  doc: TClientExtendedResponse["document"] | undefined,
): Array<File | string> {
  if (!doc) return [];
  const legacy = doc as {
    contract_signed?: string | string[] | null;
  };
  const raw = doc.signed_contract ?? legacy.contract_signed;
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.filter((x): x is string => typeof x === "string" && x.length > 0);
  }
  if (typeof raw === "string" && raw.length > 0) {
    return [raw];
  }
  return [];
}

function mapDocumentFieldsFromApi(
  ext: TClient | TClientExtended | TClientExtendedResponse,
): TClientPreRegisterFormData["document"] {
  const d = (
    "document" in ext ? ext.document : undefined
  ) as TClientExtendedResponse["document"] | undefined;
  if (!d) {
    return {
      company_logo: undefined,
      cnpj_card: undefined,
      signed_contract: [],
    } as TClientPreRegisterFormData["document"];
  }
  const logo = d.company_logo;
  const card = d.cnpj_card;
  return {
    company_logo:
      typeof logo === "string" && logo.length > 0 ? logo : undefined,
    cnpj_card: typeof card === "string" && card.length > 0 ? card : undefined,
    signed_contract: normalizeSignedContractUrls(d),
  } as TClientPreRegisterFormData["document"];
}

function mapClientToPartnerFormData(
  ext: TClient | TClientExtended | TClientExtendedResponse,
): TClientPreRegisterFormData {
  const ex = ext as TClientExtended;
  const addr = ex.address;
  const rep = ex.company_representative;
  const bank = ex.bank_account;

  return {
    name: ext.name ?? "",
    cnpj: ext.cnpj ?? "",
    corporate_name: ext.corporate_name ?? "",
    email: ext.email ?? "",
    business_classification: businessClassificationToFormValue(
      ext.business_classification as string | undefined,
    ),
    address: {
      postal_code: addr?.postal_code?.replace(/\D/g, "") ?? "",
      street: addr?.street ?? "",
      number: addr?.number ?? "",
      complement: addr?.complement ?? "",
      neighborhood: addr?.neighborhood ?? "",
      city: addr?.city ?? "",
      state: addr?.state ?? "",
    },
    company_representative: {
      first_name: rep?.first_name ?? "",
      last_name: rep?.last_name ?? "",
      personal_email: rep?.personal_email ?? "",
      cpf: rep?.cpf?.replace(/\D/g, "") ?? "",
      mobile_phone: rep?.mobile_phone?.replace(/\D/g, "") ?? "",
    },
    bank_account: {
      bank_name: bank?.bank_name ?? "",
      bank_branch: bank?.bank_branch ?? "",
      checking_account: bank?.checking_account ?? "",
      checking_account_digit: bank?.checking_account_digit ?? "",
      pix_key: bank?.pix_key ?? "",
    },
    document: mapDocumentFieldsFromApi(ext),
  } as unknown as TClientPreRegisterFormData;
}

export const useClients = (clientId?: string): IUseClients => {
  const { t } = useI18n();
  const { toast } = useToast();
  const { openDialog } = useConfirmDialog();
  const queryClient = useQueryClient();
  const location = useLocation();

  const [isClientFormOpen, setIsClientFormOpen] =
    React.useState<boolean>(false);
  const [isClientProductsFormOpen, setIsClientProductsFormOpen] =
    React.useState<boolean>(false);
  const [isClientReviewOpen, setIsClientReviewOpen] =
    React.useState<boolean>(false);
  const [isClientConfigProductsOpen, setIsClientConfigProductsOpen] =
    React.useState<boolean>(false);
  const [selectedClient, setSelectedClient] = React.useState<
    TClient | TClientExtended | null
  >(null);
  const [selectedClientId, setSelectedClientId] = React.useState<string | null>(
    null
  );

  const rejectionForm = useForm<TRejectionReason>({
    resolver: zodResolver(rejectionReasonSchema),
    defaultValues: {
      rejection_reason: "",
    },
  });

  const createPartnerForm = useForm<TClientPreRegisterFormData>({
    // @ts-expect-error - z.preprocess causes type inference issues but works correctly at runtime
    resolver: zodResolver(clientPreRegisterFormSchema),
    defaultValues: emptyPartnerFormValues(),
  });

  const editPartnerForm = useForm<TClientPreRegisterFormData>({
    // @ts-expect-error - z.preprocess causes type inference issues but works correctly at runtime
    resolver: zodResolver(clientPartnerPreRegisterEditSchema),
    defaultValues: emptyPartnerFormValues(),
  });

  const clientForm = selectedClient ? editPartnerForm : createPartnerForm;

  const documentSyncFingerprint = JSON.stringify(
    (selectedClient as TClientExtendedResponse | null)?.document ?? null,
  );

  const partnerFormSyncKey = React.useMemo(() => {
    if (!selectedClient?.id) return "";
    const ext = selectedClient as TClientExtended;
    const bank = ext.bank_account;
    return [
      selectedClient.id,
      selectedClient.name ?? "",
      selectedClient.corporate_name ?? "",
      selectedClient.cnpj ?? "",
      ext.updated_at != null ? String(ext.updated_at) : "",
      documentSyncFingerprint,
      bank?.bank_name ?? "",
      bank?.bank_branch ?? "",
      bank?.checking_account ?? "",
      bank?.checking_account_digit ?? "",
      bank?.pix_key ?? "",
    ].join("|");
  }, [
    selectedClient?.id,
    selectedClient?.name,
    selectedClient?.corporate_name,
    selectedClient?.cnpj,
    (selectedClient as TClientExtended | null)?.updated_at,
    (selectedClient as TClientExtended | null)?.bank_account,
    documentSyncFingerprint,
  ]);

  const plansHook = usePlans(selectedClient);

  React.useEffect(() => {
    const state = location.state as any;
    const load = async () => {
      if (state?.fromPlans && state?.clientId) {
        try {
          const clientData = await clientsApi.getClientById(state.clientId);
          setSelectedClient(clientData);
        } catch (error) {
          console.error("Erro ao carregar dados do cliente:", error);
          setSelectedClient({ id: state.clientId } as any);
        }

        setIsClientConfigProductsOpen(true);
        plansHook.setActiveTab("tab2");

        if (Array.isArray(state.planIds) && state.planIds.length > 0) {
          const details = await Promise.all(
            (state.planIds as string[]).map((id: string) =>
              plansApi.getPlanById(state.clientId as string, id)
            )
          );

          const planos = details.map((plano: any) => {
            const mappedProducts = (plano.products || []).map((p: any) => {
              return {
                id: p.id,
                name: p.name,
                type: p.type,
                product_id: p.product_id,
                capital_value: p.capital_value,
                premium_value: p.premium_value,
                description: p.description,
                minimum_price: p.minimum_price ?? null,
                plan_name: p.plan_name ?? null,
                insurance_policy_extra_price_id:
                  p.insurance_policy_extra_price_id ?? null,
              };
            });

            const mappedPlano = {
              id: plano.id,
              name: plano.name,
              description: plano.description,
              plan_features: Array.isArray(plano.plan_features)
                ? plano.plan_features.flat()
                : [],
              markup: plano.markup || 0,
              markup_mode: plano.markup_mode || "percentage",
              products: mappedProducts,
              commission_distribution: (
                plano.commission_distribution || []
              ).map((c: any) => ({
                agency: (c.agency || 0) / 100,
                commission: (c.commission || 0) / 100,
                position: c.position,
              })),
              commission_lifetime_other_installments:
                (plano.commission_lifetime_other_installments || 0) / 100,
              plan_price: plano.plan_price,
            };

            return mappedPlano;
          });

          plansHook.clientProductsForm.setValue("planos", planos, {
            shouldDirty: true,
          });
        }
      }
    };

    load();
  }, [location.state]);

  const attachUsersToOrganization = async (users: string[]) => {
    if (selectedClient) {
      await clientsApi.attachUsersToOrganization(selectedClient.id, users);
    }
  };

  React.useEffect(() => {
    if (selectedClient) {
      editPartnerForm.reset(mapClientToPartnerFormData(selectedClient));
    } else {
      createPartnerForm.reset(emptyPartnerFormValues());
      editPartnerForm.reset(emptyPartnerFormValues());
    }
  }, [partnerFormSyncKey]);

  const {
    data: clients,
    error: clientsError,
  } = useQuery({
    queryKey: ["clients"],
    queryFn: () => clientsApi.getAllClients(),
  });

  const { data: client, isLoading } = useQuery<TClientExtendedResponse>({
    queryKey: ["client", clientId || selectedClientId],
    queryFn: ({ queryKey }) => clientsApi.getClientById(queryKey[1] as string),
    enabled:
      (!!clientId || (isClientReviewOpen && !!selectedClientId)) &&
      !!(clientId || selectedClientId),
    staleTime: 60_000,
  });

  React.useEffect(() => {
    if (clientsError) {
      toast({
        title: t("generic.error"),
        description: clientsError.message,
        variant: "destructive",
      });
    }
  }, [clientsError, toast, t]);

  const createClientMutation = useMutation({
    mutationFn: clientsApi.createClientWithFullData,
    onSuccess: async (data) => {
      // Aprovar automaticamente o cliente após criação
      try {
        await clientsApi.approveClientData(data.id);
      } catch (error) {
        console.error("Erro ao aprovar cliente automaticamente:", error);
      }
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      setIsClientFormOpen(false);
      createPartnerForm.reset(emptyPartnerFormValues());
      editPartnerForm.reset(emptyPartnerFormValues());
      toast({
        title: t("generic.success"),
        description: t("clients.success.partnerCreated"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("clients.errors.createFailed"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const getClientByIdMutation = useMutation({
    mutationFn: (clientId: string) => clientsApi.getClientById(clientId),
    onSuccess: (data) => {
      setSelectedClient(data as TClientExtended);
    },
    onError: (error) => {
      console.error("Erro ao buscar cliente:", error);
    },
  });

  const approveClientDataMutation = useMutation({
    mutationFn: (clientId: string) => clientsApi.approveClientData(clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      toast({
        title: t("generic.success"),
        description: t("clients.success.partnerApproved"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const rejectClientDataMutation = useMutation({
    mutationFn: (data: TRejectionData) => clientsApi.rejectClientData(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      toast({
        title: t("generic.success"),
        description: t("clients.success.partnerRejected"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateClientMutation = useMutation({
    mutationFn: ({
      clientId,
      data,
    }: {
      clientId: string;
      data: TUpdateClientInput;
    }) => clientsApi.updateClient(clientId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["client", variables.clientId] });
      setSelectedClient(null);
      setIsClientFormOpen(false);
      createPartnerForm.reset(emptyPartnerFormValues());
      editPartnerForm.reset(emptyPartnerFormValues());
      toast({
        title: t("generic.success"),
        description: t("clients.success.partnerUpdated"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("clients.errors.updateFailed"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteClientMutation = useMutation({
    mutationFn: clientsApi.deleteClient,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      toast({
        title: t("generic.success"),
        description: t("clients.success.partnerDeleted"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("generic.error"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const clientOptions = React.useMemo(() => {
    if (!Array.isArray(clients)) return [];

    return clients.map((client) => ({
      value: client.id,
      label: client.name,
    }));
  }, [clients]);

  const createClient = React.useCallback(
    (data: TClientPreRegisterFormData) => {
      // Converter TClientPreRegisterFormData para TUpdateClientInput
      const clientData: TUpdateClientInput = {
        id: "",
        name: data.name,
        cnpj: data.cnpj,
        corporate_name: data.corporate_name,
        email: data.email,
        business_classification: data.business_classification as any,
        address: data.address
          ? {
              ...data.address,
              country: "BR", // País padrão Brasil
            }
          : undefined,
        company_representative: data.company_representative,
        bank_account: data.bank_account,
        document: data.document,
        created_at: new Date(),
        updated_at: new Date(),
        deleted_at: null,
        status: undefined,
        opening_date: undefined,
        signed_terms_id: undefined,
        tax_classification: undefined,
        address_id: undefined,
        organization_hierarchy: undefined as any,
      } as TUpdateClientInput & { type_partner?: any };
      
      // Adiciona type_partner se existir
      if (data.type_partner) {
        (clientData as any).type_partner = data.type_partner;
      }
      
      createClientMutation.mutate(clientData);
    },
    [createClientMutation]
  );

  const updateClient = React.useCallback(
    (clientId: string, data: TClientPreRegisterFormData) => {
      const cnpjDigits = data.cnpj.replace(/\D/g, "");
      const existingType = selectedClient?.type_partner;
      const type_partner =
        existingType === ETypeOfPartner.business_indicator
          ? ETypeOfPartner.business_indicator
          : ETypeOfPartner.client;

      const patchBody = {
        name: data.name,
        corporate_name: data.corporate_name,
        cnpj: cnpjDigits,
        type_partner,
        bank_account: {
          bank_name: data.bank_account.bank_name,
          bank_branch: data.bank_account.bank_branch,
          checking_account: data.bank_account.checking_account,
          checking_account_digit: data.bank_account.checking_account_digit,
          pix_key: data.bank_account.pix_key ?? "",
        },
      };

      updateClientMutation.mutate({
        clientId,
        data: patchBody as unknown as TUpdateClientInput,
      });
    },
    [updateClientMutation, selectedClient]
  );

  const deleteClient = React.useCallback(
    (clientId: string) => {
      const client = clients?.find((client) => client.id === clientId);
      openDialog(t("generic.confirmDelete"), client?.name || "", () =>
        deleteClientMutation.mutate(clientId)
      );
    },
    [deleteClientMutation, openDialog, t, clients]
  );

  const selectClient = React.useCallback(
    async (client: TClient | TClientExtended | TClientExtendedResponse) => {
      const ext = client as TClientExtendedResponse;
      const doc = ext.document;
      const legacyContract = doc as
        | { contract_signed?: string | string[] | null }
        | undefined;
      const hasSignedArray =
        Array.isArray(doc?.signed_contract) && doc.signed_contract.length > 0;
      const hasLegacyContract =
        typeof legacyContract?.contract_signed === "string"
          ? legacyContract.contract_signed.length > 0
          : Array.isArray(legacyContract?.contract_signed) &&
            (legacyContract?.contract_signed?.length ?? 0) > 0;
      const hasDocPayload =
        !!doc &&
        ((typeof doc.company_logo === "string" &&
          doc.company_logo.length > 0) ||
          (typeof doc.cnpj_card === "string" && doc.cnpj_card.length > 0) ||
          hasSignedArray ||
          hasLegacyContract);

      if (hasDocPayload) {
        setSelectedClient(client as TClient | TClientExtended);
      } else {
        try {
          const full = await clientsApi.getClientById(client.id);
          setSelectedClient(full as TClientExtended);
        } catch {
          setSelectedClient(client as TClient | TClientExtended);
        }
      }
      setIsClientReviewOpen(false);
      setIsClientFormOpen(true);
    },
    [],
  );

  const openClientForm = React.useCallback(() => {
    setSelectedClient(null);
    setIsClientReviewOpen(false);
    setIsClientFormOpen(true);
  }, []);

  const closeClientForm = React.useCallback(() => {
    setIsClientFormOpen(false);
    setSelectedClient(null);
    createPartnerForm.reset(emptyPartnerFormValues());
    editPartnerForm.reset(emptyPartnerFormValues());
  }, [createPartnerForm, editPartnerForm]);

  const openClientReview = React.useCallback((clientId: string) => {
    // Primeiro busca os dados, depois abre a revisão
    setSelectedClientId(clientId);
    setIsClientReviewOpen(true);
  }, []);

  const closeClientReview = React.useCallback(() => {
    setIsClientReviewOpen(false);
    setSelectedClient(null);
  }, []);

  const handleAddressBlur = React.useCallback(
    async (event: React.FocusEvent<HTMLInputElement>) => {
      const zipcode = event.target.value;
      let addressData = null;

      try {
        addressData = await clientsApi.fetchZipCode(zipcode);
      } catch (error) {
        console.error("Error fetching address:", error);
      }

      clientForm.clearErrors("address.postal_code");

      if (!zipcode) return;

      if (addressData) {
        clientForm.setValue("address.state", addressData.state_acronym);
        clientForm.setValue("address.city", addressData.city_name);
        clientForm.setValue("address.neighborhood", addressData.neighborhood_name);
        clientForm.setValue("address.street", addressData.street_full);
      } else {
        clientForm.setError("address.postal_code", {
          type: "custom",
          message: t("errors.zipcodeNotFound"),
        });
      }
    },
    [clientForm, t]
  );

  const handleClientFormSubmit = React.useCallback(
    (data: TClientPreRegisterFormData) => {
      if (selectedClient) {
        updateClient(selectedClient.id, data);
      } else {
        createClient(data);
      }
    },
    [selectedClient, updateClient, createClient]
  );

  const handleSubmitPlans = React.useCallback(
    (data: any) => {
      const state = location.state as any;
      if (state?.fromPlans) {
        plansHook.handleSubmitUpdatePlan(0);
      } else {
        plansHook.handleSubmitCreatePlan(data);
      }
    },
    [location.state, plansHook]
  );

  const submitUpdatePlan = React.useCallback(
    (index: number) => {
      plansHook.handleSubmitUpdatePlan(index);
    },
    [plansHook]
  );

  const getClientById = React.useCallback(
    (clientId: string) => {
      getClientByIdMutation.mutate(clientId);
    },
    [getClientByIdMutation]
  );

  const approveClientData = React.useCallback(
    (clientId: string) => {
      approveClientDataMutation.mutate(clientId);
    },
    [approveClientDataMutation]
  );

  const rejectClientData = React.useCallback(
    (data: TRejectionData) => {
      rejectClientDataMutation.mutate(data);
    },
    [rejectClientDataMutation]
  );

  return {
    clients: Array.isArray(clients) ? clients : [],
    client: client as TClientExtended,
    isLoading,
    isClientFormOpen,
    isClientProductsFormOpen,
    isClientReviewOpen,
    selectedClient,
    activeTab: plansHook.activeTab,
    // @ts-expect-error - z.preprocess causes type inference issues but works correctly at runtime
    clientForm,
    clientProductsForm: plansHook.clientProductsForm,
    clientProductsFormSearch: plansHook.clientProductsFormSearch,
    rejectionForm,
    clientOptions,
    createClient,
    updateClient,
    deleteClient,
    selectClient,
    openClientForm,
    closeClientForm,
    openClientReview,
    closeClientReview,
    setActiveTab: plansHook.setActiveTab,
    setIsClientFormOpen,
    setIsClientProductsFormOpen,
    setIsClientReviewOpen,
    setSelectedClient,
    handleClientFormSubmit,
    handleAddressBlur,
    getClientById,
    approveClientData,
    rejectClientData,
    setIsClientConfigProductsOpen,
    isClientConfigProductsOpen,
    filteredProducts: plansHook.filteredProducts,
    filteredUsers: plansHook.filteredUsers,
    handleSearchProducts: plansHook.handleSearchProducts,
    handleSearchUsers: plansHook.handleSearchUsers,
    clearProductsFilter: plansHook.clearProductsFilter,
    clearUsersFilter: plansHook.clearUsersFilter,
    tabs: plansHook.tabs,
    newTabName: plansHook.newTabName,
    setNewTabName: plansHook.setNewTabName,
    handleAddTab: plansHook.handleAddTab,
    addTabToForm: plansHook.addTabToForm,
    deleteTabFromForm: plansHook.deleteTabFromForm,
    handleUpdateTabLabel: plansHook.handleUpdateTabLabel,
    attachUsersToOrganization,
    handleSubmitPlans,
    submitUpdatePlan,
    isEditingPlanFlow: Boolean((location.state as any)?.fromPlans),
    setSelectedClientId,
    selectedClientId: selectedClientId || "",
  };
};
